// Story 7.10 (ADO-906) — attribute-facet wire shapes + query serialisation shared
// by the browse and search read clients (FR14/FR104). Hand-duplicated from the
// backend `search-and-discovery` FacetDto contract until the openapi-typescript
// generated client lands (7.1 D-6 posture) — keep the two in lock-step.

/** The filterable attributes (FR104). camelCase wire keys, matching the backend
 *  FacetKey (`facet.dto.ts` says what each reads). `brand`, `packSize` and
 *  `countryOfOrigin` are value facets; `newListing`, `ownBrand`, `mixedCase` and
 *  `smallCase` are flag facets whose one value is `true` (ADO-3692);
 *  `promotionalStatus` + `offerType` are offer facets that yield no values yet,
 *  and so never render. */
export type FacetKey =
  | 'brand'
  | 'packSize'
  | 'promotionalStatus'
  | 'offerType'
  | 'newListing'
  | 'ownBrand'
  | 'mixedCase'
  | 'smallCase'
  | 'countryOfOrigin'
  // The RBP not-purchased facet: its two values are the quarters a participant has not bought the
  // line in, one at a time.
  | 'rbpNotPurchased';

/**
 * The hierarchy facets: the department and category nodes (by node id, as the hierarchy read
 * returns them) holding a product in the filtered set. Facets only, never filters: the server
 * counts them while a filter is active, and the panel's Department and Category menus offer only
 * those nodes while the RBP filter is on. Lock-step with the backend `HierarchyFacetKey`.
 */
export type HierarchyFacetKey = 'department' | 'category';

/** All facet keys in display order — the single source the panel drives off. */
export const FACET_KEYS: readonly FacetKey[] = [
  'brand',
  'packSize',
  'promotionalStatus',
  'offerType',
  'newListing',
  'ownBrand',
  'mixedCase',
  'smallCase',
  'countryOfOrigin',
  'rbpNotPurchased',
];

/** Human labels for each facet section (Figma FilterPanel section headings). */
export const FACET_LABELS: Record<FacetKey, string> = {
  brand: 'Brand',
  packSize: 'Product Size',
  promotionalStatus: 'Show Me',
  offerType: 'Deals & Listing Type',
  newListing: 'Deals & Listing Type',
  ownBrand: 'Deals & Listing Type',
  mixedCase: 'Deals & Listing Type',
  smallCase: 'Deals & Listing Type',
  countryOfOrigin: 'Country of Origin',
  // The two not-purchased options sit in Show Me beside the core option.
  rbpNotPurchased: 'Show Me',
};

/**
 * Story 7.29 (ADO-2025): the catalogue narrowings, the one named set the server declares
 * (search-and-discovery `catalogue-narrowings.ts`). A listing and a search return the members its
 * range and account are offered (`narrowings`) and those with nothing to narrow in scope
 * (`unavailableNarrowings`); the rail renders the first and disables the second, so it never
 * offers an option the server does not apply. Hand-duplicated (7.1 D-6 posture): keep the two in
 * lock-step.
 */
export type CatalogueNarrowing =
  | 'category'
  | 'onOffer'
  | 'multiBuy'
  | 'leafletLine'
  | 'newListing'
  | 'ownBrand'
  | 'mixedCase'
  | 'smallCase'
  | 'catering'
  | 'brand'
  | 'packSize'
  | 'countryOfOrigin';

/**
 * The rail checkboxes wired to a facet value, by the label the rail and its chip show:
 * - the offer toggles, each by the server's offer kind (Enabler E7-C, ADO-2274: the facets'
 *   values are the MP OfferKind names, search's `listing-filters.ts` OFFER_KINDS_BY_FACET). The
 *   facets' other kinds (`WOW`, `RBP`) have no toggle yet.
 * - the listing toggles, each by its flag facet's one value `true` (ADO-3692). New Lines and New
 *   Listings drive the same filter (D-6); New Listings comes first, so the chip shows that name.
 *
 * Story 7.30 (ADO-1112), AC1: `Leaflet Lines` is the leaflet collection's own toggle, and
 * `/products?offerType=LEAFLET_LINE` is the same selection as a shareable URL — one filter over
 * the one listing, so price, stock and Add behave there exactly as everywhere else.
 */
export const FACET_TOGGLES: Readonly<Record<string, { key: FacetKey; value: string }>> = {
  'On Offer': { key: 'promotionalStatus', value: 'PROMOTION' },
  'Multi Buy': { key: 'offerType', value: 'MULTI_BUY' },
  'Leaflet Lines': { key: 'offerType', value: 'LEAFLET_LINE' },
  // Story 7.32 (ADO-1115): the lines BWG is backing this period. Offered only while the facet
  // has some (the panel never shows it inert), so nothing about Featured Lines is put to a
  // Shopper when nothing is featured for them.
  'Featured Lines': { key: 'offerType', value: 'FEATURED_LINE' },
  'New Listings': { key: 'newListing', value: 'true' },
  'New Lines': { key: 'newListing', value: 'true' },
  'Own Brand': { key: 'ownBrand', value: 'true' },
  'Mixed Case': { key: 'mixedCase', value: 'true' },
  'Small Case': { key: 'smallCase', value: 'true' },
  // The RBP toggles. `RBP` covers both RBP kinds (core and linkage child). The two not-purchased
  // options keep SL1.0's labels, the words the stores know; their facet takes one value at a time.
  'RBP Core Lines': { key: 'offerType', value: 'RBP' },
  'Show Non-Purchased Only (RBP)': { key: 'rbpNotPurchased', value: 'EITHER_QUARTER' },
  'Show Not Purchased Current Quarter (RBP)': { key: 'rbpNotPurchased', value: 'THIS_QUARTER' },
};

/** The label a selected facet value shows on its chip: a toggle's label, else the value. */
export function facetValueLabel(key: FacetKey, value: string): string {
  const toggle = Object.entries(FACET_TOGGLES).find(
    ([, mapping]) => mapping.key === key && mapping.value === value,
  );
  return toggle?.[0] ?? value;
}

/** One available value within a facet + its match count in the current set. */
export interface FacetValue {
  readonly value: string;
  readonly count: number;
}

/** A facet: its key + the available values (with counts). Zero-count values are
 *  never present, except the `offerType` facet's `RBP` and the `rbpNotPurchased` facet's two
 *  values, which an RBP participant is offered whatever their count; a facet with no values is
 *  omitted by the server (FR104). */
export interface Facet {
  readonly key: FacetKey | HierarchyFacetKey;
  readonly values: readonly FacetValue[];
}

/** The active attribute filters: facet key → selected values. AND across facets,
 *  OR within a facet's values (D-4). An absent/empty key imposes no constraint. */
export type ProductFilters = Partial<Record<FacetKey, readonly string[]>>;

/** Append the active facet filters to a URLSearchParams as comma-separated
 *  camelCase params (`?brand=Kelloggs,Nestle`), matching the backend validator's
 *  accepted wire format. Empty selections are skipped. */
export function appendFilterParams(params: URLSearchParams, filters: ProductFilters): void {
  for (const key of FACET_KEYS) {
    const values = filters[key];
    if (values !== undefined && values.length > 0) {
      params.set(key, values.map(escapeFacetValue).join(','));
    }
  }
}

/**
 * Story 7.29 (ADO-2025), D-7.29-34: a value's comma is sent as `\,` and its backslash as `\\`, so
 * a value such as "Korea, Republic of" survives the server's comma split
 * (`product-filters.validation.ts` `splitFacetValues`). A value with neither is sent as it was.
 */
export function escapeFacetValue(value: string): string {
  return value.replace(/[\\,]/g, (character) => `\\${character}`);
}

/**
 * The inverse of {@link escapeFacetValue}: split one facet param on its unescaped commas and
 * unescape `\,` and `\\`, mirroring the server's `splitFacetValues`, so a value read back
 * out of a URL `appendFilterParams` wrote (Story 7.35's shareable `/products` URL) is the value
 * that was written. A lone backslash before any other character is kept as it is.
 */
export function splitFacetValues(raw: string): string[] {
  const values: string[] = [];
  let current = '';
  for (let index = 0; index < raw.length; index += 1) {
    const character = raw.charAt(index);
    const next = raw.charAt(index + 1);
    if (character === '\\' && (next === ',' || next === '\\')) {
      current += next;
      index += 1;
    } else if (character === ',') {
      values.push(current);
      current = '';
    } else {
      current += character;
    }
  }
  values.push(current);
  return values;
}

/** Story 7.29 (ADO-2025, D-7.29-37): one key per selection, in facet order with each facet's values
 *  sorted and empty facets left out, so the same selection built in another order (a selection
 *  re-seeded from the URL) has the same key. */
export function filtersKeyOf(filters: ProductFilters): string {
  return JSON.stringify(
    FACET_KEYS.flatMap((key) => {
      const values = [...(filters[key] ?? [])].sort();
      return values.length === 0 ? [] : [[key, values]];
    }),
  );
}

/**
 * Story 7.29 (ADO-2025): whether two selections select the same values, whatever their key or
 * value order. The server echoes its applied selection in its own order.
 */
export function sameFilters(left: ProductFilters, right: ProductFilters): boolean {
  return filtersKeyOf(left) === filtersKeyOf(right);
}

/** Whether any facet has at least one selected value. */
export function hasActiveFilters(filters: ProductFilters): boolean {
  return FACET_KEYS.some((k) => (filters[k]?.length ?? 0) > 0);
}

/** Toggle a single value within a facet (add if absent, remove if present),
 *  returning a NEW filters object (never mutates). Removing the last value drops
 *  the key entirely so `hasActiveFilters` stays accurate. */
export function toggleFilterValue(
  filters: ProductFilters,
  key: FacetKey,
  value: string,
): ProductFilters {
  const current = filters[key] ?? [];
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  const out: ProductFilters = { ...filters };
  if (next.length === 0) delete out[key];
  else out[key] = next;
  return out;
}

/** Remove one value from a facet (chip dismissal) — same as toggle when present. */
export function removeFilterValue(
  filters: ProductFilters,
  key: FacetKey,
  value: string,
): ProductFilters {
  if (!(filters[key]?.includes(value) ?? false)) return filters;
  return toggleFilterValue(filters, key, value);
}

/** The flat list of active (key, value) pairs, for rendering removable chips. */
export function activeFilterChips(
  filters: ProductFilters,
): ReadonlyArray<{ key: FacetKey; value: string }> {
  const chips: Array<{ key: FacetKey; value: string }> = [];
  for (const key of FACET_KEYS) {
    for (const value of filters[key] ?? []) chips.push({ key, value });
  }
  return chips;
}
